import {
  Component,
  computed,
  inject,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { NgClass } from '@angular/common';
import { AppendTestIdPipe } from '../../../pipes/append-test-id/append-test-id.pipe';
import { TableCellComponent } from '../../../components/data-display/table/table-cell/table-cell.component';
import {
  TableComponent,
  ITableSortParams,
  ESortOrder,
  TableSortOrder,
  toSortOrder,
} from '../../../components/data-display/table/table.component';
import { TableHeaderComponent } from '../../../components/data-display/table/table-header/table-header.component';
import { TextInputComponent } from '../../../components/data-input/form-inputs/text-input/text-input.component';
import { CmsTextProviderService } from '../../../services/cms-text-provider.service';
import { SearchableHeaderComponent } from '../../../components/data-input/searchable-header/searchable-header.component';

export enum EUserStatus {
  open = 'open',
  requested = 'requested',
  active = 'active',
  deactivated = 'deactivated',
}

export interface IUserOverview {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  status: EUserStatus;
}

const MOCK_USERS: IUserOverview[] = [
  {
    userId: '96af628f-1',
    firstName: 'Björn',
    lastName: 'Haake',
    email: 'bjoern.haake@amcon.de',
    status: EUserStatus.active,
  },
  {
    userId: '7136d8bb-2',
    firstName: 'Abdelrahman',
    lastName: 'Al Ali',
    email: 'hfje593@gmail.com',
    status: EUserStatus.requested,
  },
  {
    userId: '31b6882e-3',
    firstName: 'Andrea',
    lastName: 'Seegmüller',
    email: 'andrea.seegmueller@amcon.de',
    status: EUserStatus.open,
  },
];

@Component({
  selector: 'aw-users-page',
  imports: [
    ReactiveFormsModule,
    AppendTestIdPipe,
    TableCellComponent,
    TableComponent,
    TableHeaderComponent,
    NgClass,
    SearchableHeaderComponent,
    TextInputComponent,
  ],
  templateUrl: './users-page.component.html',
  styles: ``,
})
export class UsersPageComponent {
  private readonly fb: FormBuilder = inject(FormBuilder);
  protected readonly cmsTextProvider: CmsTextProviderService = inject(
    CmsTextProviderService
  );
  public readonly userRowKey = (row: IUserOverview): string => row.userId;

  protected readonly EUserStatus: typeof EUserStatus = EUserStatus;

  private readonly userData: WritableSignal<IUserOverview[]> =
    signal<IUserOverview[]>(MOCK_USERS);
  protected readonly isRefreshing: WritableSignal<boolean> =
    signal<boolean>(false);
  private refreshInterval: number;

  protected readonly globalSearchControl: FormControl<string> =
    this.fb.nonNullable.control('');
  protected readonly firstNameControl: FormControl<string> =
    this.fb.nonNullable.control('');
  protected readonly lastNameControl: FormControl<string> =
    this.fb.nonNullable.control('');
  protected readonly emailControl: FormControl<string> =
    this.fb.nonNullable.control('');
  protected readonly userIdControl: FormControl<string> =
    this.fb.nonNullable.control('');

  private readonly globalSearch: Signal<string> = toSignal(
    this.globalSearchControl.valueChanges,
    { initialValue: '' }
  );
  private readonly firstName: Signal<string> = toSignal(
    this.firstNameControl.valueChanges,
    {
      initialValue: '',
    }
  );
  private readonly lastName: Signal<string> = toSignal(
    this.lastNameControl.valueChanges,
    {
      initialValue: '',
    }
  );
  private readonly email: Signal<string> = toSignal(
    this.emailControl.valueChanges,
    {
      initialValue: '',
    }
  );
  private readonly userId: Signal<string> = toSignal(
    this.userIdControl.valueChanges,
    {
      initialValue: '',
    }
  );

  constructor() {
    this.refresh();

    this.refreshInterval = window.setInterval(() => {
      this.refresh();
    }, 30000);
  }

  ngOnDestroy(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval); // Stoppt die automatische Aktualisierung, wenn man die Ansicht wechselt
    }
  }

  protected readonly shownUsers: Signal<IUserOverview[]> = computed(() => {
    const global: string = this.globalSearch().toLowerCase();
    return this.userData().filter((user: IUserOverview) => {
      const matchesGlobal: boolean =
        !global ||
        Object.keys(user).some((key: string) =>
          user[key as keyof IUserOverview].toLowerCase().includes(global)
        );
      const matchesColumns: boolean =
        user.firstName.toLowerCase().includes(this.firstName().toLowerCase()) &&
        user.lastName.toLowerCase().includes(this.lastName().toLowerCase()) &&
        user.email.toLowerCase().includes(this.email().toLowerCase()) &&
        user.userId.toLowerCase().includes(this.userId().toLowerCase());
      return matchesGlobal && matchesColumns;
    });
  });

  protected readonly statusLabelMap: Record<EUserStatus, string> = {
    [EUserStatus.open]: 'offen',
    [EUserStatus.requested]: 'angefragt',
    [EUserStatus.active]: 'aktiv',
    [EUserStatus.deactivated]: 'deaktiviert',
  };

  protected readonly statusClassMap: Record<EUserStatus, string> = {
    [EUserStatus.open]: 'text-yellow-600',
    [EUserStatus.requested]: 'text-blue-600',
    [EUserStatus.active]: 'text-green-600',
    [EUserStatus.deactivated]: 'text-red-600',
  };

  protected getStatusLabel(status: EUserStatus): string {
    return this.statusLabelMap[status] ?? '—';
  }

  protected getStatusClass(status: EUserStatus): string {
    return this.statusClassMap[status] ?? '';
  }

  protected refresh(): void {
    this.isRefreshing.set(true);

    setTimeout(() => {
      this.userData.set([...MOCK_USERS]);
      this.isRefreshing.set(false);
    }, 500);
  }

  public userDataFilter: ITableSortParams<IUserOverview>['compare'] = (
    a: IUserOverview,
    b: IUserOverview,
    order: TableSortOrder,
    attribute: string
  ) => {
    const dir: ESortOrder = toSortOrder(order);
    return a[attribute as keyof IUserOverview] >
      b[attribute as keyof IUserOverview]
      ? 1 * dir
      : -1 * dir;
  };
}
